import { Controller } from "@hotwired/stimulus"
import { formFingerprint, SAVE_UNCHANGED_REASON } from "../lib/form_fingerprint"

// The loading state a gl_modal frame renders server-side (.gl-modal-loading) —
// never a raw Bootstrap spinner-border, which the design system retired.
// The footer submit's reasons while it has nothing to submit (BUTTON-4).
export const FRAME_MODAL_LOADING_REASON = "Loading…"
export const FRAME_MODAL_NO_FORM_REASON = "Nothing to submit here."

export const FRAME_MODAL_LOADING_MARKUP =
  '<div class="gl-modal-loading" role="status"><div class="gl-modal-loading-spinner" aria-hidden="true"></div>' +
  '<p class="gl-modal-loading-text">Loading…</p></div>'

// The one canonical "HAML gl_modal whose body is a server form loaded into a
// Turbo Frame" controller (DESIGN.md MODAL-5 frame body). One modal serves
// every trigger on the page:
//
//   - show.bs.modal → the trigger (Bootstrap's relatedTarget) names the frame
//                     src, the modal title and the footer submit's label
//                     (#triggerConfig).
//   - hidden.bs.modal → restore the loading body and drop the src so the next
//                     open never flashes the previous record's form.
//
// Used directly by the Talk Tracks Add / Edit modal, whose triggers carry
// data-frame-modal-url (else their href), data-frame-modal-title and
// data-frame-modal-submit-label. source_edit_modal_controller extends it and
// reads its own trigger keys (data-source-url / -title). A subclass that
// overrides connect/disconnect calls super.
//
// The form submits from inside the frame, so a refused save re-renders in the
// modal; a successful save answers with a Turbo Stream page refresh (the
// server's job), which closes the modal with the page swap.
//
// A footer submit that names its form by id (`form="…"`) has nothing to submit
// until that form is in the frame: it is disabled, with its reason, while the
// frame loads and whenever the frame answers without that form — a notice
// marked [data-frame-notice] (the Get GOAT CRM popup's "seats are not on sale
// yet") gives its own text as the reason (#syncSubmits).
//
// BUTTON-9: once its form is in, the submit stays disabled until a field
// differs from the form as it FIRST loaded this open ("Change a field first").
// A refused save's re-render never re-baselines, so unsaved edits stay dirty.
// A modal whose submit is not a Save (a checkout's Continue) opts out with
// data-frame-modal-dirty-gate-value="false".
export default class extends Controller {
  static targets = ["frame", "title", "submitLabel"]
  static values = { dirtyGate: { type: Boolean, default: true } }

  connect() {
    this.baselines = new Map()
    this.element.addEventListener("show.bs.modal", this.prepare)
    this.element.addEventListener("hidden.bs.modal", this.reset)
    this.element.addEventListener("turbo:frame-load", this.syncSubmits)
    this.element.addEventListener("input", this.fieldEdited)
    this.element.addEventListener("change", this.fieldEdited)
  }

  disconnect() {
    this.element.removeEventListener("show.bs.modal", this.prepare)
    this.element.removeEventListener("hidden.bs.modal", this.reset)
    this.element.removeEventListener("turbo:frame-load", this.syncSubmits)
    this.element.removeEventListener("input", this.fieldEdited)
    this.element.removeEventListener("change", this.fieldEdited)
  }

  // What the clicked trigger asks for. Subclasses override to read other keys.
  triggerConfig(trigger) {
    const { frameModalUrl, frameModalTitle, frameModalSubmitLabel } = trigger.dataset
    return {
      url: frameModalUrl || trigger.getAttribute("href"),
      title: frameModalTitle,
      submitLabel: frameModalSubmitLabel,
    }
  }

  prepare = (event) => {
    const trigger = event.relatedTarget
    if (!trigger) return

    const { url, title, submitLabel } = this.triggerConfig(trigger)
    // Text-only spans (MODAL-1): the header icon and the button icon survive.
    if (this.hasTitleTarget && title) this.titleTarget.textContent = title
    if (this.hasSubmitLabelTarget && submitLabel) this.submitLabelTarget.textContent = submitLabel

    this.reset()
    if (url) this.frameTarget.setAttribute("src", url)
  }

  reset = () => {
    this.baselines.clear()
    this.frameTarget.removeAttribute("src")
    this.frameTarget.innerHTML = FRAME_MODAL_LOADING_MARKUP
    this.formSubmits().forEach((button) => this.setSubmitState(button, FRAME_MODAL_LOADING_REASON))
  }

  // After every frame load (a first load, a refused save's re-render) and every
  // edit: a footer submit whose form is in the frame is enabled once that form
  // is dirty; otherwise it stays disabled and says why.
  syncSubmits = () => {
    const notice = this.frameTarget.querySelector("[data-frame-notice]")
    this.formSubmits().forEach((button) => {
      const form = document.getElementById(button.getAttribute("form"))
      const ready = Boolean(form && this.frameTarget.contains(form))
      if (!ready) {
        const reason = notice ? notice.textContent.trim().replace(/\s+/g, " ") : FRAME_MODAL_NO_FORM_REASON
        this.setSubmitState(button, reason)
        return
      }
      this.setSubmitState(button, this.isDirty(form) ? null : SAVE_UNCHANGED_REASON)
    })
  }

  fieldEdited = (event) => {
    if (this.frameTarget.contains(event.target)) this.syncSubmits()
  }

  // The first load of a form this open is its baseline; later loads compare to it.
  isDirty(form) {
    if (!this.dirtyGateValue) return true
    const current = formFingerprint(form)
    if (!this.baselines.has(form.id)) this.baselines.set(form.id, current)
    return current !== this.baselines.get(form.id)
  }

  formSubmits() {
    return Array.from(this.element.querySelectorAll("button[form]"))
  }

  // gl_disabled_reason_attrs' shape (tooltip + accessible name), set and cleared
  // from here; the visible label is read each time, since a trigger can rename it.
  setSubmitState(button, reason) {
    button.disabled = Boolean(reason)
    if (reason) {
      button.setAttribute("data-gl-tooltip", reason)
      button.setAttribute("aria-label", `${button.textContent.trim().replace(/\s+/g, " ")} — ${reason}`)
    } else {
      button.removeAttribute("data-gl-tooltip")
      button.removeAttribute("aria-label")
    }
  }
}
